/**
 * Products: what we sell, by day, week or month.
 *
 * New customers by default: the New Customers page's cohort, one row per first
 * purchase, a prepaid bundle once at the price paid, so the totals match the
 * Daily Report's New Orders. Renewals and all orders are a switch away.
 *
 * Every sale carries its molecule and supply length (backend
 * services/productMix), and the page rolls them up: molecule → supply → plan,
 * supply → molecule → plan, or plan alone, plus clinic first when every
 * clinic is shown. Count, revenue and average order value, each with its share,
 * and drug cost and margin. Roll-up maths in lib/productMix.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse } from 'date-fns'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import {
  RefreshCw, AlertCircle, Loader2, ChevronRight, ChevronDown, ChevronsUpDown, X,
} from 'lucide-react'

import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button } from '../components/ui'
import { DateRangePicker } from '../components/DateRangePicker'
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover'
import { Checkbox } from '../components/ui/checkbox'
import { productMarketingAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  GROUPINGS, CLINIC_GROUPING, DIMENSION_LABELS, buildTree, chartSeries, figures, periodLabel,
} from '../lib/productMix'

const ORDER_TYPES = [
  { value: 'new', label: 'New customers', unit: 'New customers' },
  { value: 'recurring', label: 'Renewals', unit: 'Renewals' },
  { value: 'all', label: 'All orders', unit: 'Orders' },
]

const GRANULARITIES = [
  { value: 'daily', label: 'Day' },
  { value: 'weekly', label: 'Week' },
  { value: 'monthly', label: 'Month' },
]

const money = (cents, compact = false) => {
  if (cents == null || isNaN(cents)) return '—'
  const dollars = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...(compact && Math.abs(dollars) >= 1000
      ? { notation: 'compact', maximumFractionDigits: 1 }
      : { maximumFractionDigits: 0 }),
  }).format(dollars)
}
const count = n => (n == null ? '—' : Math.round(n).toLocaleString())
const pct = r => (r == null ? '—' : `${(100 * r).toFixed(r < 0.1 ? 1 : 0)}%`)

function defaultRange() {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 29)
  return { from, to }
}

function Segmented({ label, options, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex rounded-lg border border-input bg-background p-1">
        {options.map(o => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${value === o.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function CategoryFilter({ options, selected, onChange, loading }) {
  const [open, setOpen] = useState(false)
  const toggle = value => onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">Category</div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={loading}
            className="flex items-center justify-between w-full min-w-[180px] h-9 px-3 py-2 text-sm border border-input bg-background rounded-md hover:bg-accent disabled:opacity-50"
          >
            <span className="truncate text-left">
              {selected.length === 0
                ? <span className="text-muted-foreground">All categories</span>
                : selected.length === 1 ? (options.find(o => o.value === selected[0])?.label || selected[0]) : `${selected.length} selected`}
            </span>
            <span className="flex items-center gap-1 shrink-0 ml-2">
              {selected.length > 0 && (
                <span role="button" tabIndex={0} onClick={e => { e.stopPropagation(); onChange([]) }} className="p-0.5 hover:bg-muted rounded">
                  <X className="h-3 w-3 text-muted-foreground" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <div className="max-h-[300px] overflow-y-auto p-2 space-y-1">
            {options.length === 0 && <div className="py-4 text-center text-sm text-muted-foreground">No categories</div>}
            {options.map(o => (
              <label key={o.value} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer">
                <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
                <span className="text-sm truncate">{o.label}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

function Tile({ title, value, note }) {
  return (
    <Card>
      <CardContent className="p-4 space-y-0.5">
        <div className="text-xs text-muted-foreground">{title}</div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {note && <div className="text-xs text-muted-foreground truncate">{note}</div>}
      </CardContent>
    </Card>
  )
}

/** One table row and, when open, its children. */
function TreeRows({ node, parentTotal, periods, measure, open, toggle, color }) {
  const f = figures(node, parentTotal)
  const isOpen = open.has(node.id)
  const canOpen = node.children.length > 0
  const periodValue = p => {
    const v = node.byPeriod[p]?.[measure] || 0
    return measure === 'revenue' ? money(v, true) : count(v)
  }
  return (
    <>
      <tr className={`border-t ${node.depth === 0 ? '' : 'text-muted-foreground'} hover:bg-muted/40`}>
        <td className="py-1.5 pr-2 sticky left-0 bg-background">
          <button
            type="button"
            onClick={() => canOpen && toggle(node.id)}
            className={`flex items-center gap-1.5 text-left ${canOpen ? '' : 'cursor-default'}`}
            style={{ paddingLeft: `${node.depth * 1.25}rem` }}
          >
            {canOpen ? (isOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />) : <span className="w-3.5 shrink-0" />}
            {color && <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />}
            <span className={`truncate max-w-[18rem] ${node.dimension === 'plan' ? 'font-mono text-xs' : node.depth === 0 ? 'font-medium text-foreground' : ''}`} title={node.key}>{node.label}</span>
          </button>
        </td>
        <td className="py-1.5 px-2 text-right">{count(f.count)}</td>
        <td className="py-1.5 px-2 text-right">{pct(f.countShare)}</td>
        <td className="py-1.5 px-2 text-right">{money(f.revenue)}</td>
        <td className="py-1.5 px-2 text-right">{pct(f.revenueShare)}</td>
        <td className="py-1.5 px-2 text-right">{money(f.aov)}</td>
        <td className="py-1.5 px-2 text-right">{money(f.cogs)}</td>
        <td className="py-1.5 px-2 text-right">{pct(f.marginRate)}</td>
        {periods.map(p => <td key={p} className="py-1.5 px-2 text-right border-l">{periodValue(p)}</td>)}
      </tr>
      {isOpen && node.children.map(child => (
        <TreeRows key={child.id} node={child} parentTotal={node.total} periods={periods} measure={measure} open={open} toggle={toggle} />
      ))}
    </>
  )
}

export default function ProductMarketingPage() {
  const { currentClinicId } = useAuthStore()
  const allClinics = currentClinicId === null
  const [searchParams, setSearchParams] = useSearchParams()

  // Several keys in ONE update. react-router v6's functional setSearchParams
  // does not chain: two calls in a row each start from the current URL, so the
  // second silently drops the first — which is how the date picker lost
  // `start` and fell back to the default range.
  const setParams = useCallback(values => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(values)) {
        if (Array.isArray(value)) { next.delete(key); value.forEach(v => next.append(key, v)) } else next.set(key, value)
      }
      return next
    }, { replace: true })
  }, [setSearchParams])
  const setParam = useCallback((key, value) => setParams({ [key]: value }), [setParams])

  const granularity = searchParams.get('granularity') || 'daily'
  const orderType = searchParams.get('orderType') || 'new'
  const groupings = allClinics ? [...GROUPINGS, CLINIC_GROUPING] : GROUPINGS
  const grouping = groupings.find(g => g.key === searchParams.get('groupBy')) || groupings[0]
  const measure = searchParams.get('measure') === 'revenue' ? 'revenue' : 'count'
  const share = searchParams.get('share') === '1'
  const categories = useMemo(() => searchParams.getAll('categories'), [searchParams])
  const categoriesKey = categories.join(',')

  const dateRange = useMemo(() => {
    const start = searchParams.get('start')
    const end = searchParams.get('end')
    if (start && end) {
      const from = parse(start, 'yyyy-MM-dd', new Date())
      const to = parse(end, 'yyyy-MM-dd', new Date())
      if (!isNaN(from) && !isNaN(to)) return { from, to }
    }
    return defaultRange()
  }, [searchParams])
  const startDate = format(dateRange.from, 'yyyy-MM-dd')
  const endDate = format(dateRange.to, 'yyyy-MM-dd')

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [categoryOptions, setCategoryOptions] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [open, setOpen] = useState(() => new Set())
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (allClinics) return
    let alive = true
    setCategoriesLoading(true)
    productMarketingAPI.getFilters()
      .then(r => {
        if (!alive || !r.data?.success) return
        setCategoryOptions((r.data.data.categories || []).map(c => ({ value: c.category_key, label: c.display_name || c.category_key })))
      })
      .catch(() => {})
      .finally(() => { if (alive) setCategoriesLoading(false) })
    return () => { alive = false }
  }, [allClinics, currentClinicId])

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    const params = { startDate, endDate, granularity, orderType }
    const request = allClinics
      ? productMarketingAPI.getMixAggregate(params)
      : productMarketingAPI.getMix({ ...params, categories: categoriesKey ? categoriesKey.split(',') : [] })
    request
      .then(r => { if (alive && r.data?.success) setData(r.data.data) })
      .catch(err => { if (alive) setError(err.response?.data?.error?.message || err.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [allClinics, currentClinicId, startDate, endDate, granularity, orderType, categoriesKey, reloadKey])

  // A new grouping is a new tree; open rows from the old one mean nothing.
  useEffect(() => { setOpen(new Set()) }, [grouping.key])

  const cells = data?.cells
  const periods = useMemo(() => data?.periods || [], [data])
  const tree = useMemo(() => (cells ? buildTree(cells, grouping.path, measure) : null), [cells, grouping.path, measure])
  const chart = useMemo(() => (tree ? chartSeries(tree, periods, { measure, share }) : null), [tree, periods, measure, share])
  const colorByNode = useMemo(() => new Map((chart?.series || []).filter(s => s.node).map(s => [s.node.id, s.color])), [chart])

  const unit = ORDER_TYPES.find(o => o.value === orderType).unit
  const total = tree ? figures(tree, null) : null
  const top = tree?.children[0]
  const toggle = id => setOpen(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">Products</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            {orderType === 'new'
              ? 'What new customers buy: one row per first purchase, bundles once at the price paid. Matches the Daily Report’s New Orders.'
              : orderType === 'recurring' ? 'Paid renewal orders by product.' : 'New customers and paid renewals by product.'}
            {' '}Eastern time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={range => {
              if (!range?.from || !range?.to) return
              setParams({ start: format(range.from, 'yyyy-MM-dd'), end: format(range.to, 'yyyy-MM-dd') })
            }}
          />
          <Button variant="outline" size="icon" onClick={() => setReloadKey(k => k + 1)} disabled={loading} className="h-9 w-9" aria-label="Refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <Segmented label="Show" options={ORDER_TYPES} value={orderType} onChange={v => setParam('orderType', v)} />
            <Segmented label="By" options={GRANULARITIES} value={granularity} onChange={v => setParam('granularity', v)} />
            <Segmented label="Group" options={groupings.map(g => ({ value: g.key, label: g.label }))} value={grouping.key} onChange={v => setParam('groupBy', v)} />
            {!allClinics && <CategoryFilter options={categoryOptions} selected={categories} onChange={v => setParam('categories', v)} loading={categoriesLoading} />}
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="p-6">
          <div className="flex items-center gap-3 text-sm">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <span className="flex-1">Could not load the product mix: {error}</span>
            <Button size="sm" variant="outline" onClick={() => setReloadKey(k => k + 1)}>Retry</Button>
          </div>
        </Card>
      )}
      {data?.truncated && (
        <p className="text-xs text-destructive">This range hit the row limit, so the oldest sales are missing. Narrow the dates for complete figures.</p>
      )}
      {data?.failed?.length > 0 && (
        <p className="text-xs text-destructive">Not included, failed to load: {data.failed.join(', ')}.</p>
      )}

      {!data && loading && (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      )}

      {tree && (
        <div className={`space-y-4 sm:space-y-6 ${loading ? 'opacity-60' : ''}`}>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <Tile title={unit} value={count(total.count)} note={top ? `${top.label} ${pct(top.total.count / (total.count || 1))}` : null} />
            <Tile title="Revenue" value={money(total.revenue, true)} />
            <Tile title="Average order" value={money(total.aov)} />
            <Tile title="Margin after drug cost" value={pct(total.marginRate)} note={`${money(total.cogs, true)} drug cost`} />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Mix by {GRANULARITIES.find(g => g.value === granularity).label.toLowerCase()}</CardTitle>
                  <CardDescription className="text-xs">
                    {DIMENSION_LABELS[grouping.path[0]]}, {share ? 'as a share of each period' : measure === 'revenue' ? 'revenue' : unit.toLowerCase()}.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Segmented label="" options={[{ value: 'count', label: unit }, { value: 'revenue', label: 'Revenue' }]} value={measure} onChange={v => setParam('measure', v)} />
                  <Segmented label="" options={[{ value: '0', label: 'Total' }, { value: '1', label: 'Share' }]} value={share ? '1' : '0'} onChange={v => setParam('share', v)} />
                </div>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 pt-2 text-xs text-muted-foreground">
                {chart.series.map(s => (
                  <span key={s.key} className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                    {s.label}
                  </span>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {tree.total.count === 0 ? (
                <p className="py-12 text-center text-sm text-muted-foreground">No sales in this range.</p>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart.rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} tickFormatter={p => periodLabel(p, granularity)} minTickGap={12} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        width={52}
                        domain={share ? [0, 100] : [0, 'auto']}
                        tickFormatter={v => (share ? `${Math.round(v)}%` : measure === 'revenue' ? `$${v >= 1000 ? `${Math.round(v / 1000)}k` : Math.round(v)}` : v)}
                      />
                      <Tooltip
                        labelFormatter={p => periodLabel(p, granularity)}
                        formatter={v => (share ? `${v.toFixed(1)}%` : measure === 'revenue' ? `$${Math.round(v).toLocaleString()}` : Math.round(v).toLocaleString())}
                      />
                      {chart.series.map((s, i) => (
                        <Bar key={s.key} dataKey={s.key} name={s.label} stackId="mix" fill={s.color} radius={i === chart.series.length - 1 ? [2, 2, 0, 0] : 0} isAnimationActive={false} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Breakdown</CardTitle>
              <CardDescription className="text-xs">
                Open a row to drill into {grouping.path.slice(1).map(d => DIMENSION_LABELS[d].toLowerCase()).join(', then ') || 'it'}.
                Shares are of the row above. Drug cost is the pharmacy price per fill, times the fills a prepaid bundle includes.
                Period columns show {measure === 'revenue' ? 'revenue' : unit.toLowerCase()}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm tabular-nums whitespace-nowrap">
                  <thead>
                    <tr className="text-xs text-muted-foreground">
                      <th className="py-2 pr-2 text-left font-medium sticky left-0 bg-background">{grouping.path.map(d => DIMENSION_LABELS[d]).join(' / ')}</th>
                      <th className="py-2 px-2 text-right font-medium">{unit}</th>
                      <th className="py-2 px-2 text-right font-medium">Share</th>
                      <th className="py-2 px-2 text-right font-medium">Revenue</th>
                      <th className="py-2 px-2 text-right font-medium">Rev. share</th>
                      <th className="py-2 px-2 text-right font-medium">AOV</th>
                      <th className="py-2 px-2 text-right font-medium">Drug cost</th>
                      <th className="py-2 px-2 text-right font-medium">Margin</th>
                      {periods.map(p => <th key={p} className="py-2 px-2 text-right font-medium border-l">{periodLabel(p, granularity)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {tree.children.map(group => (
                      <TreeRows
                        key={group.id}
                        node={group}
                        parentTotal={tree.total}
                        periods={periods}
                        measure={measure}
                        open={open}
                        toggle={toggle}
                        color={colorByNode.get(group.id)}
                      />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 font-medium">
                      <td className="py-2 pr-2 sticky left-0 bg-background">Total</td>
                      <td className="py-2 px-2 text-right">{count(total.count)}</td>
                      <td className="py-2 px-2 text-right">100%</td>
                      <td className="py-2 px-2 text-right">{money(total.revenue)}</td>
                      <td className="py-2 px-2 text-right">100%</td>
                      <td className="py-2 px-2 text-right">{money(total.aov)}</td>
                      <td className="py-2 px-2 text-right">{money(total.cogs)}</td>
                      <td className="py-2 px-2 text-right">{pct(total.marginRate)}</td>
                      {periods.map(p => (
                        <td key={p} className="py-2 px-2 text-right border-l">
                          {measure === 'revenue' ? money(tree.byPeriod[p]?.revenue || 0, true) : count(tree.byPeriod[p]?.count || 0)}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
